import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useCreateCost, useUpdateCost, useDeleteCost, useCosts } from "../../../lib/supabase/costs/supabaseCostService";
import { DateTime } from "luxon";
import { Button } from "../../ui/button";
import { ChevronLeft, ChevronRight, Plus, Trash2, Pencil, AlertTriangle, Users } from "lucide-react";
import { Breadcrumbs } from "../../ui/breadcrumbs";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { SheetTable } from "../../SheetTable";
import { formatCurrency } from "../../../utils/format";
import { getDefaultDisplayMonth } from "../../../utils/monthDisplay";
import { Skeleton } from "../../ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { useForm, Controller } from "react-hook-form";
import { useModels } from "../../../lib/supabase/models/supabaseModelService";
import { SplitType, calculateSplitCounts, calculateSplitAmounts } from "../miscpaymentsview/splitUtils";
import { Alert, AlertDescription, AlertTitle } from "../../ui/alert";
import { Cost, CostCategory } from "@shiftly/shared/src/schema";
import { UserAssignmentsDialog } from "./UserAssignmentsDialog";
import { useCommissionAssignments } from "../../../lib/supabase/commission-model-assignments/supabaseCommissionModelAssignmentService";
import { useMiscModelSplits } from "../../../lib/supabase/misc-payments/supabaseMiscModelSplitsService";

interface CostFormData {
  name: string;
  set: number;
  amount: number;
  category: CostCategory;
  split_type: SplitType;
  set1_percentage: number;
  set2_percentage: number;
  notes?: string;
}

interface EditCostDialogProps {
  cost: Cost;
  onUpdate: (cost: Cost) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  all_costs: Cost[];
  selected_month: DateTime;
}

interface CostAssignment {
  id: string;
  name: string;
  assigned_models: Array<{
    model_id: string;
    monthly_amounts: Record<string, number>; // Key format: "YYYY-MM"
  }>;
}

// Type for grouped cost data (combines split costs)
interface GroupedCost {
  id: string;
  name: string;
  amount: number;
  category: CostCategory;
  set: number | string; // Can be number for single cost or string for split display
  created_at: DateTime;
  updated_at: DateTime;
  metadata?: Cost['metadata'];
  is_split: boolean;
  split_costs?: Cost[]; // Original costs for split entries
  original_cost?: Cost; // Original cost for single entries
}

const EditCostDialog = ({ cost, onUpdate, open, onOpenChange, all_costs: all_costs, selected_month: selected_month }: EditCostDialogProps) => {
  const { data: models = [] } = useModels();
  const { data: savedSplits } = useMiscModelSplits(selected_month);

  // Check if selected month is in the past
  const current_month = getDefaultDisplayMonth('Europe/London');
  const isPastMonth = selected_month.startOf('month') < current_month.startOf('month');

  // Check if this is a split cost
  const is_splitCost = !!cost.metadata?.split_id;

  // Get split percentages from existing split costs if applicable
  const getCurrentSplitPercentages = useCallback(() => {
    if (is_splitCost) {
      const relatedCosts = all_costs.filter(c => c.metadata?.split_id === cost.metadata?.split_id);
      if (relatedCosts.length === 2) {
        const total_amount = relatedCosts.reduce((sum, c) => sum + c.amount, 0);
        const set1Cost = relatedCosts.find(c => c.set === 1);
        if (set1Cost && total_amount > 0) {
          const set1_percentage = Math.round((set1Cost.amount / total_amount) * 100);
          const set2_percentage = 100 - set1_percentage;
          return { set1_percentage, set2_percentage };
        }
      }
    }

    // Default to model-based percentages
    const splitCounts = calculateSplitCounts(models, 'all', savedSplits?.data);
    const defaultset1_percentage = splitCounts.totalModels > 0 ? Math.round((splitCounts.set1Count / splitCounts.totalModels) * 100) : 50;
    const defaultset2_percentage = splitCounts.totalModels > 0 ? Math.round((splitCounts.set2Count / splitCounts.totalModels) * 100) : 50;
    return { set1_percentage: defaultset1_percentage, set2_percentage: defaultset2_percentage };
  }, [is_splitCost, all_costs, cost.metadata?.split_id, models, savedSplits]);

  // Get total amount for split costs
  const getTotalAmount = useCallback(() => {
    if (is_splitCost) {
      const relatedCosts = all_costs.filter(c => c.metadata?.split_id === cost.metadata?.split_id);
      return relatedCosts.reduce((sum, c) => sum + c.amount, 0);
    }
    return cost.amount;
  }, [is_splitCost, all_costs, cost.metadata?.split_id, cost.amount]);

  const initialPercentages = useMemo(() => {
    return getCurrentSplitPercentages();
  }, [getCurrentSplitPercentages]);

  const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm<CostFormData>({
    defaultValues: {
      name: cost.name,
      set: cost.set,
      amount: getTotalAmount(),
      category: cost.category,
      split_type: is_splitCost ? 'all' : 'none', // Set split_type based on current split status
      set1_percentage: initialPercentages.set1_percentage,
      set2_percentage: initialPercentages.set2_percentage,
      notes: cost.metadata?.notes || ''
    }
  });

  // Update form values when allCosts data loads
  useEffect(() => {
    if (all_costs.length > 0) {
      const total_amount = getTotalAmount();
      setValue('amount', total_amount);
    }
  }, [all_costs, getTotalAmount, setValue, is_splitCost, cost.metadata?.split_id, cost.created_at]);

  const split_type = watch('split_type');

  // Update split percentages when models data changes or split type changes
  useEffect(() => {
    if (split_type !== 'none') {
      const splitCounts = calculateSplitCounts(models, split_type, savedSplits?.data);
      if (splitCounts.totalModels > 0) {
        const new_set1_percentage = Math.round((splitCounts.set1Count / splitCounts.totalModels) * 100);
        const new_set2_percentage = Math.round((splitCounts.set2Count / splitCounts.totalModels) * 100);
        setValue('set1_percentage', new_set1_percentage);
        setValue('set2_percentage', new_set2_percentage);
      }
    }
  }, [split_type, models, setValue, savedSplits]);

  const onSubmit = handleSubmit((data: CostFormData) => {
    // Handle different scenarios based on split type changes
    const wasOriginallySplit = is_splitCost;
    const isNowSplit = data.split_type !== 'none';

    if (wasOriginallySplit && !isNowSplit) {
      // Converting from split to single cost
      // This requires special handling - we'll just update with a note about the conversion
      onUpdate({
        ...cost,
        name: data.name,
        set: Number(data.set),
        amount: Number(data.amount),
        category: data.category,
        metadata: {
          notes: data.notes,
          // Remove split_id to convert to single cost
          split_id: undefined
        }
      });
    } else if (!wasOriginallySplit && isNowSplit) {
      // Converting from single to split cost
      // This would create a new split, pass special flag to parent
      onUpdate({
        ...cost,
        name: data.name,
        amount: Number(data.amount),
        category: data.category,
        metadata: {
          notes: data.notes
        },
        // Add special properties to indicate this should become a split
        __convertToSplit: true,
        __split_type: data.split_type,
        __set1_percentage: data.set1_percentage,
        __set2_percentage: data.set2_percentage
      } as any);
    } else if (wasOriginallySplit && isNowSplit) {
      // Updating existing split cost
      onUpdate({
        ...cost,
        name: data.name,
        amount: Number(data.amount),
        category: data.category,
        metadata: {
          ...cost.metadata,
          notes: data.notes
        },
        // Add special properties to indicate split amount update
        __updateSplit: true,
        __set1_percentage: data.set1_percentage,
        __set2_percentage: data.set2_percentage
      } as any);
    } else {
      // Regular single cost update
      onUpdate({
        ...cost,
        name: data.name,
        set: Number(data.set),
        amount: Number(data.amount),
        category: data.category,
        metadata: {
          ...cost.metadata,
          notes: data.notes
        }
      });
    }

    onOpenChange(false);
  });

  // Get current split counts for display
  const currentSplitCounts = calculateSplitCounts(models, split_type, savedSplits?.data);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{is_splitCost ? 'Edit Split Cost' : 'Edit Cost'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Name and Category row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && (
                <p className="text-sm text-brand-red">{errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Controller
                name="category"
                control={control}
                rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Commission">Commission</SelectItem>
                      <SelectItem value="Tools">Tools</SelectItem>
                      <SelectItem value="Referral">Referral</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.category && (
                <p className="text-sm text-brand-red">{errors.category.message}</p>
              )}
            </div>
          </div>

          {/* Split Type Selection */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="split_type">Split Type</Label>
              <Controller
                name="split_type"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select split type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Split</SelectItem>
                      <SelectItem value="all">All Models</SelectItem>
                      <SelectItem value="uk">UK Models</SelectItem>
                      <SelectItem value="fp">FP Models</SelectItem>
                      <SelectItem value="gg_swap">GG Swap</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {split_type !== 'none' ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Auto-calculated based on active {split_type === 'all' ? 'all' : split_type === 'gg_swap' ? 'GG SWAP' : split_type.toUpperCase()} model counts:
                  Set 1 ({currentSplitCounts.set1Count} models), Set 2 ({currentSplitCounts.set2Count} models)
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="set1_percentage">Set 1 Percentage</Label>
                    <div className="flex items-center gap-2">
                      <Controller
                        name="set1_percentage"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="number"
                            value={field.value || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              const numValue = value === '' ? 0 : Math.min(100, Math.max(0, parseFloat(value) || 0));
                              const newSet2 = Math.max(0, 100 - numValue);
                              field.onChange(numValue);
                              setValue('set2_percentage', newSet2);
                            }}
                            min={0}
                            max={100}
                            step={1}
                            className="flex-1"
                          />
                        )}
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="set2_percentage">Set 2 Percentage</Label>
                    <div className="flex items-center gap-2">
                      <Controller
                        name="set2_percentage"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="number"
                            value={field.value || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              const numValue = value === '' ? 0 : Math.min(100, Math.max(0, parseFloat(value) || 0));
                              const newSet1 = Math.max(0, 100 - numValue);
                              field.onChange(numValue);
                              setValue('set1_percentage', newSet1);
                            }}
                            min={0}
                            max={100}
                            step={1}
                            className="flex-1"
                          />
                        )}
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="set">Set</Label>
                <Input
                  id="set"
                  type="number"
                  {...register("set", {
                    required: "Set is required",
                    min: { value: 1, message: "Set must be at least 1" }
                  })}
                />
                {errors.set && (
                  <p className="text-sm text-brand-red">{errors.set.message}</p>
                )}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="amount">Amount (£)</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              {...register("amount", {
                required: "Amount is required",
              })}
            />
            {errors.amount && (
              <p className="text-sm text-brand-red">{errors.amount.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register("notes")}
              placeholder="Optional notes"
            />
          </div>
          {isPastMonth && (
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>
                You are editing a cost for {selected_month.toFormat('MMMM yyyy')}, which is in the past.
                Please ensure this is intentional.
              </AlertDescription>
            </Alert>
          )}
          <Button type="submit" className="w-full">Update Cost</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const CostsView = () => {
  const [selected_month, setSelectedMonth] = useState<DateTime>(() => {
    return getDefaultDisplayMonth('Europe/London');
  });

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<Cost | null>(null);
  const [isUserAssignmentsOpen, setIsUserAssignmentsOpen] = useState(false);
  const [assignments, setAssignments] = useState<CostAssignment[]>([]);
  const hasLoadedAssignments = useRef(false);

  const { data: costs = [], isLoading } = useCosts(selected_month);
  const { data: models = [] } = useModels();
  const { data: existingAssignments = [] } = useCommissionAssignments();
  const { data: savedSplits } = useMiscModelSplits(selected_month);

  // Load existing assignments when they change
  useEffect(() => {
    if (existingAssignments.length > 0 && !hasLoadedAssignments.current) {
      const transformedAssignments: CostAssignment[] = existingAssignments.map(assignment => ({
        id: assignment.id,
        name: assignment.name,
        assigned_models: assignment.assigned_models.map(model => ({
          model_id: model.model_id,
          monthly_amounts: model.monthly_amounts || {}
        }))
      }));

      setAssignments(transformedAssignments);
      hasLoadedAssignments.current = true;
    }
  }, [existingAssignments.length]);

  // Handle case when there are no assignments
  useEffect(() => {
    if (existingAssignments.length === 0 && hasLoadedAssignments.current) {
      setAssignments([]);
      hasLoadedAssignments.current = false;
    }
  }, [existingAssignments.length]);

  const createCost = useCreateCost();
  const updateCost = useUpdateCost();
  const deleteCost = useDeleteCost();

  // Get split counts for default percentages
  const getDefaultSplitPercentages = useCallback(() => {
    const splitCounts = calculateSplitCounts(models, 'all', savedSplits?.data);
    const defaultset1_percentage = splitCounts.totalModels > 0 ? Math.round((splitCounts.set1Count / splitCounts.totalModels) * 100) : 50;
    const defaultset2_percentage = splitCounts.totalModels > 0 ? Math.round((splitCounts.set2Count / splitCounts.totalModels) * 100) : 50;
    return { defaultset1_percentage, defaultset2_percentage };
  }, [models, savedSplits]);

  const { register, handleSubmit, control, watch, setValue, reset, formState: { errors } } = useForm<CostFormData>({
    defaultValues: {
      category: 'Other',
      split_type: 'none',
      set1_percentage: getDefaultSplitPercentages().defaultset1_percentage,
      set2_percentage: getDefaultSplitPercentages().defaultset2_percentage,
      set: 1
    }
  });

  const split_type = watch('split_type');

  // Update split percentages when models data changes or split type changes
  useEffect(() => {
    if (split_type !== 'none') {
      const splitCounts = calculateSplitCounts(models, split_type, savedSplits?.data);
      if (splitCounts.totalModels > 0) {
        const newset1_percentage = Math.round((splitCounts.set1Count / splitCounts.totalModels) * 100);
        const newset2_percentage = Math.round((splitCounts.set2Count / splitCounts.totalModels) * 100);
        setValue('set1_percentage', newset1_percentage);
        setValue('set2_percentage', newset2_percentage);
      }
    }
  }, [split_type, models, setValue, savedSplits]);

  // Group costs by split_id
  const groupedCosts = useMemo(() => {
    const grouped: GroupedCost[] = [];
    const processedSplitIds = new Set<string>();

    costs.forEach(cost => {
      const split_id = cost.metadata?.split_id;

      if (split_id && !processedSplitIds.has(split_id)) {
        // This is a split cost and we haven't processed this split yet
        const split_costs = costs.filter(c => c.metadata?.split_id === split_id);
        processedSplitIds.add(split_id);

        // Calculate totals
        const total_amount = split_costs.reduce((sum, c) => sum + c.amount, 0);

        // Calculate percentages for display
        const setPercentages = split_costs.map(c => {
          const percentage = total_amount > 0 ? Math.round((c.amount / total_amount) * 100) : 0;
          return `${c.set} (${percentage}%)`;
        }).join(' ');

        grouped.push({
          id: split_id,
          name: cost.name,
          amount: total_amount,
          category: cost.category,
          set: setPercentages,
          created_at: cost.created_at,
          updated_at: cost.updated_at,
          metadata: {
            ...cost.metadata,
            notes: split_costs[0].metadata?.notes || undefined
          },
          is_split: true,
          split_costs
        });
      } else if (!split_id) {
        // This is a regular cost (not split)
        grouped.push({
          id: cost.id,
          name: cost.name,
          amount: cost.amount,
          category: cost.category,
          set: cost.set,
          created_at: cost.created_at,
          updated_at: cost.updated_at,
          metadata: cost.metadata,
          is_split: false,
          original_cost: cost
        });
      }
    });

    return grouped;
  }, [costs]);

  const columns = [
    {
      key: "name" as keyof GroupedCost,
      header: "Name",
      type: "text" as const,
      width: "50%",
      editable: false
    },
    {
      key: "set" as keyof GroupedCost,
      header: "Set",
      type: "text" as const,
      width: "20%",
      editable: false,
      render: (value: number | string) => value.toString()
    },
    {
      key: "amount" as keyof GroupedCost,
      header: "Amount",
      type: "currency" as const,
      width: "20%",
      editable: false,
      render: (value: number) => formatCurrency(value, '£')
    },
    {
      key: "actions" as keyof GroupedCost,
      header: "",
      type: "custom" as const,
      width: "10%",
      editable: false,
      render: (_: any, item?: GroupedCost) => item && (
        <div className="flex items-center justify-end gap-2">
          {item.is_split ? (
            // For split costs, show single edit/delete button
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setEditingCost(item.split_costs![0])}
                className="h-8 w-8 p-0"
                title="Edit split cost"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  // Delete all split costs
                  item.split_costs?.forEach(cost => {
                    deleteCost.mutate({ id: cost.id });
                  });
                }}
                className="h-8 w-8 p-0"
                title="Delete split cost"
              >
                <Trash2 className="h-4 w-4 text-brand-red" />
              </Button>
            </>
          ) : (
            // For single costs, show regular edit/delete buttons
            item.original_cost && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditingCost(item.original_cost!)}
                  className="h-8 w-8 p-0"
                  title="Edit cost"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteCost.mutate({ id: item.original_cost!.id })}
                  className="h-8 w-8 p-0"
                  title="Delete cost"
                >
                  <Trash2 className="h-4 w-4 text-brand-red" />
                </Button>
              </>
            )
          )}
        </div>
      )
    }
  ];

  const onSubmit = (data: CostFormData) => {
    if (data.split_type !== 'none') {
      // Generate a unique split_id for this split
      const split_id = crypto.randomUUID();

      // Calculate split amounts using utility function
      const { set1Amount, set2Amount } = calculateSplitAmounts(
        Number(data.amount),
        0, // No bonus for costs
        data.set1_percentage
      );

      // Create cost for set 1
      createCost.mutate({
        cost: {
          name: data.name,
          set: 1,
          amount: set1Amount,
          category: data.category,
          metadata: {
            split_id: split_id,
            notes: data.notes
          }
        },
        month: selected_month
      });

      // Create cost for set 2
      createCost.mutate({
        cost: {
          name: data.name,
          set: 2,
          amount: set2Amount,
          category: data.category,
          metadata: {
            split_id: split_id,
            notes: data.notes
          }
        },
        month: selected_month
      });
    } else {
      // Create single cost
      createCost.mutate({
        cost: {
          name: data.name,
          set: Number(data.set),
          amount: Number(data.amount),
          category: data.category,
          metadata: {
            notes: data.notes
          }
        },
        month: selected_month
      });
    }

    reset({
      category: 'Other',
      split_type: 'none',
      set1_percentage: getDefaultSplitPercentages().defaultset1_percentage,
      set2_percentage: getDefaultSplitPercentages().defaultset2_percentage,
      set: 1
    });
    setIsAddDialogOpen(false);
  };

  // Get current split counts for display
  const currentSplitCounts = calculateSplitCounts(models, split_type, savedSplits?.data);

  const handleDelete = (index: number) => {
    const item = groupedCosts[index];
    if (item.is_split && item.split_costs) {
      // For split costs, delete all split costs
      item.split_costs.forEach(cost => {
        deleteCost.mutate({ id: cost.id });
      });
    } else if (item.original_cost) {
      // For single costs, delete the original cost
      deleteCost.mutate({ id: item.original_cost.id });
    }
  };

  // Check if selected month is in the past
  const currentMonth = getDefaultDisplayMonth('Europe/London');
  const isPastMonth = selected_month.startOf('month') < currentMonth.startOf('month');

  const breadcrumbs = [
    { title: 'Home', href: '/' },
    { title: 'Costs' }
  ];

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className="flex justify-between">
          <div className="space-y-1">
            <Breadcrumbs items={breadcrumbs} className="mb-2" />
            <h1 className="text-3xl font-bold tracking-tight text-brand-dark dark:text-foreground">
              Costs
            </h1>
            <p className="text-muted-foreground dark:text-muted-foreground">
              View and manage costs
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle></CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedMonth(prev => prev.minus({ months: 1 }).startOf('month'))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-lg font-semibold min-w-[200px] text-center">
                {selected_month.toFormat('MMM d, yyyy')} - {selected_month.endOf('month').toFormat('MMM d, yyyy')}
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedMonth(prev => prev.plus({ months: 1 }).startOf('month'))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedMonth(DateTime.utc().startOf('month'))}
              >
                Today
              </Button>
              <Button variant="outline" size="sm" onClick={() => {
                hasLoadedAssignments.current = false;
                setIsUserAssignmentsOpen(true);
              }}>
                <Users className="h-4 w-4" />
                User Commission
              </Button>
              <Button variant="default" size="sm" onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Add Cost
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-full rounded" />
              <Skeleton className="h-8 w-full rounded" />
              <Skeleton className="h-8 w-full rounded" />
            </div>
          ) : groupedCosts.length === 0 ? (
            <div className="flex h-[100px] items-center justify-center rounded-md border border-dashed bg-white dark:bg-brand-dark">
              <p className="text-sm text-muted-foreground">No costs found</p>
            </div>
          ) : (
            <SheetTable
              data={groupedCosts}
              columns={columns}
              showTotals={true}
              groupBy={{ key: "category" as keyof GroupedCost }}
              onDelete={handleDelete}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Cost</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && (
                <p className="text-sm text-brand-red">{errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Controller
                name="category"
                control={control}
                rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Commission">Commission</SelectItem>
                      <SelectItem value="Tools">Tools</SelectItem>
                      <SelectItem value="Referral">Referral</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.category && (
                <p className="text-sm text-brand-red">{errors.category.message}</p>
              )}
            </div>

            {/* Split Type Selection */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="split_type">Split Type</Label>
                <Controller
                  name="split_type"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select split type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Split</SelectItem>
                        <SelectItem value="all">All Models</SelectItem>
                        <SelectItem value="uk">UK Models</SelectItem>
                        <SelectItem value="fp">FP Models</SelectItem>
                        <SelectItem value="gg_swap">GG Swap</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              {split_type !== 'none' ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Auto-calculated based on active {split_type === 'all' ? 'all' : split_type === 'gg_swap' ? 'GG SWAP' : split_type.toUpperCase()} model counts:
                    Set 1 ({currentSplitCounts.set1Count} models), Set 2 ({currentSplitCounts.set2Count} models)
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="set1_percentage">Set 1 Percentage</Label>
                      <div className="flex items-center gap-2">
                        <Controller
                          name="set1_percentage"
                          control={control}
                          render={({ field }) => (
                            <Input
                              type="number"
                              value={field.value || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                const numValue = value === '' ? 0 : Math.min(100, Math.max(0, parseFloat(value) || 0));
                                const newSet2 = Math.max(0, 100 - numValue);
                                field.onChange(numValue);
                                setValue('set2_percentage', newSet2);
                              }}
                              min={0}
                              max={100}
                              step={1}
                              className="flex-1"
                            />
                          )}
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="set2_percentage">Set 2 Percentage</Label>
                      <div className="flex items-center gap-2">
                        <Controller
                          name="set2_percentage"
                          control={control}
                          render={({ field }) => (
                            <Input
                              type="number"
                              value={field.value || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                const numValue = value === '' ? 0 : Math.min(100, Math.max(0, parseFloat(value) || 0));
                                const newSet1 = Math.max(0, 100 - numValue);
                                field.onChange(numValue);
                                setValue('set1_percentage', newSet1);
                              }}
                              min={0}
                              max={100}
                              step={1}
                              className="flex-1"
                            />
                          )}
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="set">Set</Label>
                  <Input
                    id="set"
                    type="number"
                    {...register("set", {
                      required: "Set is required",
                      min: { value: 1, message: "Set must be at least 1" }
                    })}
                  />
                  {errors.set && (
                    <p className="text-sm text-brand-red">{errors.set.message}</p>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount">Amount (£)</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                {...register("amount", {
                  required: "Amount is required",
                })}
              />
              {errors.amount && (
                <p className="text-sm text-brand-red">{errors.amount.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                {...register("notes")}
                placeholder="Optional notes"
              />
            </div>
            {isPastMonth && (
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Warning</AlertTitle>
                <AlertDescription>
                  You are adding a cost for {selected_month.toFormat('MMMM yyyy')}, which is in the past.
                  Please ensure this is intentional.
                </AlertDescription>
              </Alert>
            )}
            <Button type="submit" className="w-full">Add Cost</Button>
          </form>
        </DialogContent>
      </Dialog>

      {editingCost && (
        <EditCostDialog
          cost={editingCost}
          all_costs={costs}
          selected_month={selected_month}
          onUpdate={(updatedCost: any) => {
            const split_id = editingCost.metadata?.split_id;

            if (updatedCost.__convertToSplit) {
              // Converting single cost to split
              // Delete the original cost first
              deleteCost.mutate({ id: editingCost.id });
              
              // Calculate split amounts
              const { set1Amount, set2Amount } = calculateSplitAmounts(
                updatedCost.amount,
                0,
                updatedCost.__set1_percentage
              );

              // Generate new split_id
              const newSplitId = crypto.randomUUID();

              // Create split costs
              createCost.mutate({
                cost: {
                  name: updatedCost.name,
                  set: 1,
                  amount: set1Amount,
                  category: updatedCost.category,
                  metadata: {
                    split_id: newSplitId,
                    notes: updatedCost.metadata?.notes
                  }
                },
                month: selected_month
              });

              createCost.mutate({
                cost: {
                  name: updatedCost.name,
                  set: 2,
                  amount: set2Amount,
                  category: updatedCost.category,
                  metadata: {
                    split_id: newSplitId,
                    notes: updatedCost.metadata?.notes
                  }
                },
                month: selected_month
              });
            } else if (updatedCost.__updateSplit && split_id) {
              // Updating existing split cost amounts
              const relatedCosts = costs.filter(c => c.metadata?.split_id === split_id);

              // Calculate new split amounts
              const { set1Amount, set2Amount } = calculateSplitAmounts(
                updatedCost.amount,
                0,
                updatedCost.__set1_percentage
              );

              // Update each cost in the split
              relatedCosts.forEach(relatedCost => {
                const newAmount = relatedCost.set === 1 ? set1Amount : set2Amount;
                updateCost.mutate({
                  id: relatedCost.id,
                  cost: {
                    name: updatedCost.name,
                    amount: newAmount,
                    category: updatedCost.category,
                    metadata: {
                      ...relatedCost.metadata,
                      notes: updatedCost.metadata?.notes
                    }
                  },
                  month: selected_month
                });
              });
            } else if (split_id && !updatedCost.metadata?.split_id) {
              // Converting split to single cost
              // Delete all related costs first
              const relatedCosts = costs.filter(c => c.metadata?.split_id === split_id);
              relatedCosts.forEach(relatedCost => {
                deleteCost.mutate({ id: relatedCost.id });
              });

              // Create new single cost
              createCost.mutate({
                cost: {
                  name: updatedCost.name,
                  set: updatedCost.set,
                  amount: updatedCost.amount,
                  category: updatedCost.category,
                  metadata: {
                    notes: updatedCost.metadata?.notes
                  }
                },
                month: selected_month
              });
            } else if (split_id) {
              // Regular split cost update (name, category, notes only)
              const relatedCosts = costs.filter(c => c.metadata?.split_id === split_id);
              relatedCosts.forEach(relatedCost => {
                updateCost.mutate({
                  id: relatedCost.id,
                  cost: {
                    name: updatedCost.name,
                    category: updatedCost.category,
                    metadata: {
                      ...relatedCost.metadata,
                      notes: updatedCost.metadata?.notes
                    }
                  },
                  month: selected_month
                });
              });
            } else {
              // Regular single cost update
              updateCost.mutate({
                id: editingCost.id,
                cost: {
                  name: updatedCost.name,
                  set: updatedCost.set,
                  amount: updatedCost.amount,
                  category: updatedCost.category,
                  metadata: updatedCost.metadata
                },
                month: selected_month
              });
            }
            setEditingCost(null);
          }}
          open={!!editingCost}
          onOpenChange={(open) => !open && setEditingCost(null)}
        />
      )}

      <UserAssignmentsDialog
        open={isUserAssignmentsOpen}
        onOpenChange={setIsUserAssignmentsOpen}
        assignments={assignments}
        onAssignmentsChange={setAssignments}
        models={models}
        selected_month={selected_month.toFormat('yyyy-MM')}
      />
    </div>
  );
}; 